import { useState } from "react";
import { Play, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";
import { VIDEO_TESTIMONIALS } from "../lib/site-data";

export default function VideoTestimonials() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="testimonials"
      data-testid="video-testimonials-section"
      className="py-24 md:py-32 bg-background"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="label-eyebrow">Client Stories</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-tight max-w-2xl">
              Real people. Real interviews. Real offers.
            </h2>
          </div>
          <p className="text-foreground/70 max-w-md">
            Watch how our clients landed their dream roles after a CV makeover.
            Tap a video to play.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {VIDEO_TESTIMONIALS.map((v, i) => (
            <button
              key={v.id}
              data-testid={`video-thumb-${v.id}`}
              onClick={() => setActive(v)}
              className="group relative aspect-[3/4] overflow-hidden rounded-2xl bg-black text-left ring-1 ring-border hover:ring-brand-400 transition"
            >
              <img
                src={v.poster}
                alt={v.name}
                loading="lazy"
                decoding="async"
                width="640"
                height="853"
                className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent pointer-events-none" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="h-14 w-14 rounded-full bg-white/95 text-black flex items-center justify-center shadow-xl group-hover:scale-110 transition">
                  <Play className="h-6 w-6 fill-current" />
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3 text-white pointer-events-none">
                <p className="text-sm font-semibold">{v.name}</p>
                <p className="text-xs text-white/80 leading-tight">{v.role}</p>
              </div>
              <div className="absolute top-3 left-3 text-[10px] tracking-widest uppercase text-white/80 bg-black/40 px-2 py-1 rounded-full pointer-events-none">
                #{i + 1}
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="video-dialog"
          className="max-w-3xl p-0 bg-black border-0 overflow-hidden"
        >
          <DialogTitle className="sr-only">
            {active?.name} — video testimonial
          </DialogTitle>
          {active && (
            <div className="relative">
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                data-testid="video-dialog-close"
                className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white rounded-full p-2"
              >
                <X className="h-5 w-5 text-black" />
              </button>
              <video
                key={active.id}
                src={active.src}
                poster={active.poster}
                controls
                autoPlay
                playsInline
                className="w-full max-h-[80vh] bg-black"
                data-testid="video-player"
              />
              <div className="p-5 bg-black text-white">
                <p className="font-semibold">{active.name}</p>
                <p className="text-sm text-white/70">{active.role}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
